import { createRef, Component } from 'react'
import { shape, string, instanceOf } from 'prop-types'
import { CSSTransition } from 'react-transition-group'

import Settings from '../../../../../models/settings.js'
import GridFeedPost from './grid-feed-post.js'

const DISPLAY_PHASES = {
  SHOW: 'show',
  TEASE: 'tease',
  HIDE_POST: 'hide-post',
  HIDE_WRAPPER: 'hide-wrapper',
}

const DEFAULT_TRANSITION = {
  classNames: 'show-display-post',
  appear: true,
  timeout: 500,
  in: true,
  unmountOnExit: true,
}

function getTargetSizes() {
  const targetSize = {
    width: window.innerWidth * 0.75,
    maxHeight: '85%',
  }

  // the items are square so it's okay to calculate this like this
  if (targetSize.width > window.innerHeight * 0.85) {
    targetSize.width = window.innerHeight * 0.85
  }
  return targetSize
}

class GridDisplayPost extends Component {
  static propTypes = {
    containerElement: instanceOf(HTMLElement).isRequired,
    feed: shape().isRequired,
    feedKey: string.isRequired,
    settings: instanceOf(Settings).isRequired,
  }

  constructor(props) {
    super(props)

    this.state = {
      displayPost: null,
      displayPostRect: null,
      displayPostPhase: null,
    }

    this.nodeRef = createRef()

    this.intervals = []
    this.timeouts = []

    this.findDisplayPostInterval = this.findDisplayPostInterval.bind(this)
    this.findDisplayPost = this.findDisplayPost.bind(this)
  }

  componentDidMount() {
    this.findDisplayPostInterval()
  }

  componentDidUpdate(prevProps) {
    const { settings } = this.props
    const { settings: prevSettings } = prevProps

    if (settings.highlightTimeout !== prevSettings.highlightTimeout) {
      this.timeouts = []
      this.findDisplayPostInterval()
    }
  }

  componentWillUnmount() {
    this.intervals.forEach((interval) => clearInterval(interval))
    this.timeouts.forEach((timeout) => clearTimeout(timeout))
  }

  findDisplayPost() {
    const { containerElement, feed, settings } = this.props

    // Finding a random visible post:
    // 1. get all posts
    // 2. shuffle them
    // 3. iterate until we find a visible one
    const allVisiblePosts = [
      ...containerElement.querySelectorAll('.bzfy-c-tilegrid-post-container'),
    ]
    const shuffledPosts = []
    for (let i = allVisiblePosts.length; i > 0; i -= 1) {
      const index = Math.floor(Math.random() * allVisiblePosts.length)
      shuffledPosts.push(allVisiblePosts[index])
      allVisiblePosts.splice(index, 1)
    }

    const displayPost = shuffledPosts.find((el) => el.getBoundingClientRect())
    if (!displayPost) {
      return
    }

    const ht = settings.highlightTimeout * 1000
    const rect = displayPost.getBoundingClientRect()
    const { postKey } = displayPost.dataset
    this.setState({
      displayPost: feed.posts.find((p) => p.key === postKey),
      displayPostRect: {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      },
    })

    this.timeouts = [
      setTimeout(() => {
        this.setState({
          displayPostPhase: DISPLAY_PHASES.TEASE,
        })
      }, 500),

      setTimeout(() => {
        this.setState({
          displayPostPhase: DISPLAY_PHASES.SHOW,
        })
      }, 1000),

      setTimeout(() => {
        this.setState({
          displayPostPhase: DISPLAY_PHASES.HIDE_POST,
        })
      }, 1000 + ht),

      setTimeout(
        () => {
          this.setState({
            displayPostPhase: DISPLAY_PHASES.HIDE_WRAPPER,
          })
        },
        1000 + ht + 499,
      ),

      setTimeout(
        () => {
          this.setState({
            displayPost: null,
            displayPostPhase: null,
          })
        },
        1000 + ht + 999,
      ),
    ]
  }

  findDisplayPostInterval() {
    this.intervals.push(
      setInterval(
        () => {
          this.findDisplayPost()
        },
        this.props.settings.highlightTimeout * 1000 + 7500,
      ),
    )
  }

  render() {
    const { feedKey, settings } = this.props
    const { displayPost, displayPostRect, displayPostPhase } = this.state

    if (!displayPost) {
      return null
    }

    const targetSize = getTargetSizes()

    const displayPostStyles =
      displayPostPhase !== DISPLAY_PHASES.SHOW
        ? displayPostRect
        : {
            ...targetSize,
            top: '50%',
            left: '50%',
            transform: 'translateY(-50%) translateX(-50%)',
          }

    if (displayPostPhase === DISPLAY_PHASES.HIDE_POST) {
      displayPostStyles.opacity = 0
    }

    return displayPostPhase !== DISPLAY_PHASES.HIDE_WRAPPER ? (
      <CSSTransition nodeRef={this.nodeRef} {...DEFAULT_TRANSITION}>
        <div ref={this.nodeRef} className="bzfy-c-tile-grid-display-post-wrapper">
          <GridFeedPost
            additionalStyles={displayPostStyles}
            feedKey={feedKey}
            locale={settings.locale}
            post={displayPost}
            settings={settings}
            width={targetSize.width}
          />
        </div>
      </CSSTransition>
    ) : null
  }
}

export default GridDisplayPost
